/**
 * ProvenanceTimeline — ruled vertical list of sealed service history entries.
 *
 * Design spec:
 *   - <ol>/<li> ruled list, NOT cards per entry.
 *   - Newest first.
 *   - Each row: 64px left rail (coarsened date in mono) + 1fr content (title + parts).
 *   - 1px hairline left rule threads the timeline.
 *   - NO cost, mileage, vendor, exact date slots — defense in depth.
 *   - "YYYY-MM" coarsened dates rendered via <time datetime="YYYY-MM"> with
 *     full month name as visible text for accessibility.
 *   - Parts rendered as neutral mono tags.
 *
 * Spec 011 Slice 3 — owner-provided before/after photos:
 *   - `entry.photos[]` may carry a `role: 'before' | 'after'` pair. Always
 *     matched BY ROLE (never positionally — array order is not guaranteed).
 *   - A photo may be present with no role (older/general data) — that photo
 *     is handled entirely upstream (the generic gallery), never here.
 *   - Rendered in its own bordered sub-block, visually SEPARATE from the row's
 *     title/date — neutral "Owner photo" pill + an explicit "not
 *     independently verified" disclaimer. Must never read as sealed/verified
 *     evidence: no green, no seal/shield iconography, no "Fig." captions.
 *   - FOUNDER-RATIFIED: no labor-hours/duration value is rendered here (the
 *     public payload never carries it — see spec 011 D1/D4). Do not add it.
 *   - Fail-closed: a tombstoned/shredded photo just 404s on load (existing
 *     dossier photo-serve behavior) — no new error UI is added for it.
 *
 * Accessibility:
 *   - <time datetime> provides machine-readable date for AT.
 *   - Section heading "Service history" with entry count as caption.
 *   - Ordered list conveys sequential order to AT.
 *   - The "not independently verified" disclaimer renders in `T.text` (not
 *     `T.textMuted`) to clear 4.5:1 contrast — see plan.md a11y note.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No Web APIs without Capacitor equivalents.
 */

import { Pill } from '@/components/Pill';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import type { PublicDossierEntry, PublicDossierPhoto } from '@/types/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a "YYYY-MM" string to { month: "January", year: "2024" }.
 * Falls back to rendering the raw string if parsing fails.
 */
function parseCoarsenedDate(yyyyMm: string): { month: string; year: string; raw: string } {
  const parts = yyyyMm.split('-');
  if (parts.length < 2) return { month: yyyyMm, year: '', raw: yyyyMm };
  const year = parts[0] ?? '';
  const monthStr = parts[1] ?? '';
  const monthIdx = parseInt(monthStr, 10) - 1;
  if (!year || isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) {
    return { month: yyyyMm, year: '', raw: yyyyMm };
  }
  const date = new Date(Date.UTC(parseInt(year, 10), monthIdx, 1));
  return {
    month: date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' }),
    year,
    raw: yyyyMm,
  };
}

/**
 * Pick the before/after pair from an entry's photos, matched explicitly BY
 * ROLE. Never assume array position — order is not guaranteed on the wire.
 * Photos with no role (older/general data) are ignored here entirely; they
 * are handled by the generic gallery upstream, not this component.
 */
function pickBeforeAfter(photos: PublicDossierPhoto[] | undefined): {
  before: PublicDossierPhoto | undefined;
  after: PublicDossierPhoto | undefined;
} {
  const list = photos ?? [];
  return {
    before: list.find((p) => p.role === 'before'),
    after: list.find((p) => p.role === 'after'),
  };
}

// ---------------------------------------------------------------------------
// OwnerPhotoPair — spec 011 Slice 3 before/after evidence, owner-provided.
//
// Deliberately visually distinct from the sealed row above it: neutral pill,
// no green, no shield/seal iconography, plain "Before"/"After" captions
// (never "Fig. 0N" — that convention is reserved for the documentary
// gallery). The disclaimer text is `T.text`, not `T.textMuted`, per the
// a11y note in the file header (4.5:1 contrast on a load-bearing trust claim).
// ---------------------------------------------------------------------------

function OwnerPhotoPair({
  photos,
  entryTitle,
}: {
  photos: PublicDossierPhoto[] | undefined;
  entryTitle: string;
}) {
  const { before, after } = pickBeforeAfter(photos);
  if (!before && !after) return null;

  const columns = before && after ? '1fr 1fr' : '1fr';

  return (
    <div
      style={{
        marginTop: SPACE.sm,
        paddingTop: SPACE.sm,
        borderTop: `1px solid ${T.border}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: SPACE.xs,
          marginBottom: SPACE.sm,
        }}
      >
        <Pill variant="neutral" size="sm">
          Owner photo
        </Pill>
        <span style={{ fontFamily: F.body, fontSize: 12, color: T.text, lineHeight: 1.4 }}>
          Not independently verified
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: columns,
          gap: SPACE.sm,
          maxWidth: 360,
        }}
      >
        {before && (
          <figure style={{ margin: 0 }}>
            <img
              src={before.url}
              alt={`Before — ${entryTitle}`}
              loading="lazy"
              style={{
                width: '100%',
                aspectRatio: '4 / 3',
                objectFit: 'cover',
                borderRadius: RADIUS.md,
                border: `1px solid ${T.border}`,
                display: 'block',
              }}
            />
            <figcaption
              style={{
                fontFamily: F.mono,
                fontSize: 10,
                color: T.textMuted,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginTop: SPACE.xs,
              }}
            >
              Before
            </figcaption>
          </figure>
        )}
        {after && (
          <figure style={{ margin: 0 }}>
            <img
              src={after.url}
              alt={`After — ${entryTitle}`}
              loading="lazy"
              style={{
                width: '100%',
                aspectRatio: '4 / 3',
                objectFit: 'cover',
                borderRadius: RADIUS.md,
                border: `1px solid ${T.border}`,
                display: 'block',
              }}
            />
            <figcaption
              style={{
                fontFamily: F.mono,
                fontSize: 10,
                color: T.textMuted,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginTop: SPACE.xs,
              }}
            >
              After
            </figcaption>
          </figure>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProvenanceTimelineProps {
  entries: PublicDossierEntry[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProvenanceTimeline({ entries }: ProvenanceTimelineProps) {
  // Newest first — entries are YYYY-MM strings; sort descending.
  const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <section aria-labelledby="timeline-heading">
      {/* Section header */}
      <div style={{ marginBottom: SPACE.md }}>
        <h2
          id="timeline-heading"
          style={{
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Service history
        </h2>
        <p
          style={{
            fontFamily: F.mono,
            fontSize: 11,
            color: T.textMuted,
            margin: `${SPACE.xs}px 0 0`,
            letterSpacing: '0.04em',
          }}
        >
          {entries.length} sealed {entries.length === 1 ? 'entry' : 'entries'}
        </p>
      </div>

      {entries.length === 0 ? (
        /*
         * OGILVY FLAG: empty-history copy. Neutral statement of fact; no implication
         * the record is incomplete or the owner is hiding anything.
         */
        <p
          style={{
            fontFamily: F.body,
            fontSize: 13,
            color: T.textMuted,
            lineHeight: 1.6,
          }}
        >
          No service history has been added to this record yet.
        </p>
      ) : (
        <ol
          aria-label="Service history timeline"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            borderLeft: `1px solid ${T.border}`,
            paddingLeft: SPACE.md,
          }}
        >
          {sorted.map((entry, idx) => {
            const { month, year, raw } = parseCoarsenedDate(entry.date);
            return (
              <li
                key={`${entry.date}-${entry.title}-${idx}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '64px 1fr',
                  gap: `0 ${SPACE.md}px`,
                  paddingBottom: idx < sorted.length - 1 ? SPACE.md : 0,
                  marginBottom: idx < sorted.length - 1 ? SPACE.md : 0,
                  borderBottom: idx < sorted.length - 1 ? `1px solid ${T.border}` : 'none',
                }}
              >
                {/* Left rail — coarsened date */}
                <div style={{ flexShrink: 0 }}>
                  <time
                    dateTime={raw}
                    style={{
                      display: 'block',
                      fontFamily: F.mono,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    aria-label={`${month} ${year}`}
                  >
                    {/* Visible text: abbreviated month over year */}
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'block',
                        fontSize: 10,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: T.textMuted,
                        lineHeight: 1.2,
                      }}
                    >
                      {month.slice(0, 3)}
                    </span>
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'block',
                        fontSize: 12,
                        color: T.text,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        marginTop: 2,
                      }}
                    >
                      {year}
                    </span>
                  </time>
                </div>

                {/* Right content */}
                <div>
                  <p
                    style={{
                      fontFamily: F.body,
                      fontSize: 14,
                      fontWeight: 500,
                      color: T.text,
                      margin: 0,
                      lineHeight: 1.4,
                    }}
                  >
                    {entry.title}
                  </p>
                  {(entry.parts?.length ?? 0) > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: SPACE.xs,
                        marginTop: SPACE.xs,
                      }}
                    >
                      {(entry.parts ?? []).map((part, pIdx) => (
                        <span
                          key={pIdx}
                          style={{
                            fontFamily: F.mono,
                            fontSize: 11,
                            color: T.textMuted,
                            background: `color-mix(in srgb, ${T.border} 40%, transparent)`,
                            border: `1px solid ${T.border}`,
                            borderRadius: RADIUS.sm,
                            padding: `1px ${SPACE.xs}px`,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {part}
                        </span>
                      ))}
                    </div>
                  )}
                  <OwnerPhotoPair photos={entry.photos} entryTitle={entry.title} />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
